import { Autocomplete, Box, Checkbox, Chip, Grid, Stack, TextField, Typography } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import React, { useEffect, useMemo, useState } from 'react';
import { BackButton, Button, ErrorMessage, Heading, Input, Select, UploadImage } from '../../components/Common';
import { useLocation, useNavigate } from 'react-router-dom';
import { bloodGroupList, countryList } from '../../helper';
import { useMessage } from '../../components/Common/Message/MessageContext';
import { useGetCityListQuery, useGetStateListQuery } from '../../services/Location';
import {
  useAddNewSubAdminMutation,
  useUpdateSubAdminMutation,
  useGetAdminByCityQuery,
  useGetSubAdminDetailQuery,
  useGetExtraPincodeByCityQuery,
  useGetPincodeByCityQuery,
} from '../../services/SubAdmin';
import { useGetAdminDetailQuery } from '../../services/Admin';
import { getAllData } from '../../helper/indexDB';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const AddPincodeAdmin = () => {
  const { showMessage } = useMessage();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState([]);
  const [country, setCountry] = useState(1);
  const [states, setStates] = useState(null);
  const [city, setCity] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [aadharImage, setAadharImage] = useState([]);
  const [bloodGroup, setBloodGroup] = useState(null);
  const [selectedPincodes, setSelectedPincodes] = useState([]);

  const [formValues, setFormValues] = useState({
    name: '',
    mobile: '',
    email: '',
    guardianName: '',
    guardianMobile: '',
    address: '',
    panNo: '',
    dob: '',
  });

  const [errorMessages, setErrorMessages] = useState({});
  const [profileErrorMessage, setProfileErrorMessage] = useState('');
  const [addharErrorMessage, setAddharErrorMessage] = useState('');

  const { data: cityLst, error: cityError, isLoading: cityLoading } = useGetCityListQuery(states, { skip: !states });
  const cityList = cityLst?.data;
  const { data: subAdminLst, error: subAdminError, isLoading: subAdminLoading } = useGetSubAdminDetailQuery(state?.id, { skip: !state, refetchOnMountOrArgChange: true });
  const subAdminDetails = subAdminLst?.data;


  const { data: stateLst, error: stateError, isLoading: stateLoading } = useGetStateListQuery();
  const stateList = stateLst?.data;

  const { data: adminLst, error: adminListError, isLoading: adminListLoading } = useGetAdminByCityQuery();
  const adminList = adminLst?.AdminData;

  const selectedAdmin = useMemo(() => {
    if (!admin || !adminList?.length) return null;
    return adminList.find((a) => String(a.id) === String(admin)) || null;
  }, [admin, adminList]);

  const isSelectedAdminSuper = useMemo(() => {
    return String(selectedAdmin?.type) === '1';
  }, [selectedAdmin]);

  // Fetch admin detail to get pincodes assigned to the selected admin
  const { data: adminDetailData, isLoading: adminDetailLoading } = useGetAdminDetailQuery(admin, {
    skip: !admin,
    refetchOnMountOrArgChange: true,
  });

  // Extract pincodes from admin detail response
  const adminPincodes = useMemo(() => {
    const pins = adminDetailData?.data?.pincodes || [];
    return pins
      .filter((p) => p) // remove null/undefined
      .map((pincode) => ({ pincode: String(pincode) }));
  }, [adminDetailData]);

  // If selected admin is Super Admin, show all pincodes from the selected city
  const { data: cityExtraPincodeRes, isLoading: cityExtraPincodeLoading } = useGetExtraPincodeByCityQuery(city, {
    skip: !city || !isSelectedAdminSuper,
    refetchOnMountOrArgChange: true,
  });

  const cityPincodesForSuperAdmin = useMemo(() => {
    const rows = cityExtraPincodeRes?.data || [];
    return rows.map((r) => ({ pincode: String(r.pincode) }));
  }, [cityExtraPincodeRes]);

  // Fetch pincode ownership info to disable already-assigned pincodes (any PinCodeAdmin)
  const targetCityForOwnership = city;
  const { data: pincodeOwnershipData, isLoading: pincodeOwnershipLoading } = useGetPincodeByCityQuery(
    targetCityForOwnership,
    {
      skip: !targetCityForOwnership,
      refetchOnMountOrArgChange: true,
    }
  );

  // Map pincode -> pinCodeAdminId (null if not assigned to any PinCodeAdmin)
  const pincodeOwnershipMap = useMemo(() => {
    const map = {};
    const list = pincodeOwnershipData?.data || [];
    list.forEach((item) => {
      const pin = String(item.pincode || '');
      if (pin) {
        map[pin] = item.pinCodeAdminId || null;
      }
    });
    return map;
  }, [pincodeOwnershipData]);

  const [adminRole, setAdminRole] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      const storedData = await getAllData();
      // setUser(storedData?.[0]?.value.id);
      setAdminRole(storedData?.[0]?.value.type);

      if (storedData?.[0]?.value.type !== 1) {
        setCity(storedData?.[0]?.value.city);
        setStates(storedData?.[0]?.value.state);
        setAdmin(storedData?.[0]?.value.id);
      }
    };

    fetchData();
  }, []);


  const handleStateChange = (e) => setStates(e.target.value)
  const handleCountryChange = (e) => setCountry(e.target.value)
  const handleCityChange = (e) => setCity(e.target.value)
  const handleBloodGroupChange = (e) => setBloodGroup(e.target.value);
  
  const handleAdminChange = (e) => {
    const selectedId = parseInt(e.target.value);
    setAdmin(selectedId);

    const selected = adminList?.find(item => String(item.id) === String(selectedId));
    // Only auto-set state/city for non-super-admin selections.
    // For Super Admin selection, user should pick the city from dropdown and see all city pincodes.
    if (selected && String(selected.type) !== '1') {
      setCity(selected.city);
      setStates(selected.state);
    }
    
    // Reset pincodes when admin changes
    setSelectedPincodes([]);
  }

  const [addAdmin] = useAddNewSubAdminMutation();
  const [updateAdmin] = useUpdateSubAdminMutation();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const validateField = (field, fieldName) => {
    if (!field) {
      setErrorMessages(prevState => ({ ...prevState, [fieldName]: `${fieldName} is a mandatory field.` }));
      return true;
    }
    setErrorMessages(prevState => ({ ...prevState, [fieldName]: '' }));
    return false;
  };

  // When admin pincodes load, preselect ONLY free pincodes (not already assigned to any PinCodeAdmin) in add mode
  useEffect(() => {
    // Skip in edit mode - pincodes are set from subAdminDetails
    if (state?.id) return;

    if (!admin) {
      setSelectedPincodes([]);
      return;
    }

    // If Super Admin selected: preselect all FREE pincodes for the selected city (when available)
    if (isSelectedAdminSuper) {
      if (!city || !cityPincodesForSuperAdmin.length) {
        setSelectedPincodes([]);
        return;
      }
      const freePins = cityPincodesForSuperAdmin
        .filter((p) => !pincodeOwnershipMap[p.pincode]) // only those not assigned to any PinCodeAdmin
        .map((p) => p.pincode);
      setSelectedPincodes(freePins);
      return;
    }

    // Non-super admin: preselect ONLY admin's pincodes that are not yet assigned to any PinCodeAdmin
    if (!adminPincodes.length) {
      setSelectedPincodes([]);
      return;
    }
    const freeAdminPins = adminPincodes
      .filter((p) => !pincodeOwnershipMap[p.pincode]) // not assigned to any PinCodeAdmin
      .map((p) => p.pincode);
    setSelectedPincodes(freeAdminPins);
  }, [admin, adminPincodes, state?.id, isSelectedAdminSuper, city, cityPincodesForSuperAdmin, pincodeOwnershipMap]);

  const handleFormSubmit = async () => {
    // Reset all error messages
    setErrorMessages({});
    setProfileErrorMessage('');
    setAddharErrorMessage('');
  
    // Validation for mandatory fields
    let hasError = false;
    
    // ✅ FIXED: Check if profileImage is a File object, array with File, or existing URL (edit mode)
    const hasProfileImage = 
      profileImage instanceof File || 
      (Array.isArray(profileImage) && profileImage.length > 0 && profileImage[0] instanceof File) ||
      (typeof profileImage === 'string' && profileImage.length > 0); // Edit mode: URL string
    
    const hasAadharImage = 
      aadharImage instanceof File || 
      (Array.isArray(aadharImage) && aadharImage.length > 0 && aadharImage[0] instanceof File) ||
      (typeof aadharImage === 'string' && aadharImage.length > 0); // Edit mode: URL string
  
    // Only validate images in ADD mode (when state is null)
    if (!state?.id) {
      if (!hasProfileImage) {
        setProfileErrorMessage('Please select a profile image to upload.');
        hasError = true;
      }
  
      if (!hasAadharImage) {
        setAddharErrorMessage('Please select an aadhar image to upload.');
        hasError = true;
      }
    }
  
    // Validate pincodes - require at least one if admin is selected
    let pincodeError = false;
    if (admin && selectedPincodes.length === 0) {
      setErrorMessages(prev => ({ ...prev, pincode: 'Please select at least one pincode assigned to this admin.' }));
      pincodeError = true;
      hasError = true;
    } else {
      setErrorMessages(prev => ({ ...prev, pincode: '' }));
    }
  
    // Validate fields
    const nameError = validateField(formValues.name, 'name');
    const adminError = validateField(admin, 'admin');
    const mobileError = validateField(formValues.mobile, 'mobile');
    const emailError = validateField(formValues.email, 'email');
    const guardianNameError = validateField(formValues.guardianName, 'guardianName');
    const guardianMobileError = validateField(formValues.guardianMobile, 'guardianMobile');
    const countryError = validateField(country, 'country');
    const stateError = validateField(states, 'state');
    const cityError = validateField(city, 'city');
    const addressError = validateField(formValues.address, 'address');
    const panNoError = validateField(formValues.panNo, 'panNo');
    const dobError = validateField(formValues.dob, 'dob');
    const bloodGroupError = validateField(bloodGroup, 'bloodGroup');
  
    // If any field has an error, prevent form submission
    if (
      nameError || mobileError || emailError || guardianNameError || guardianMobileError ||
      countryError || stateError || cityError || addressError || hasError ||
      panNoError || dobError || bloodGroupError || adminError || pincodeError
    ) {
      return;
    }
  
    const formData = new FormData();
    
    // ✅ FIXED: Only append images if they are File objects (new uploads)
    // If profileImage is a File or array with File
    if (profileImage instanceof File) {
      formData.append('profileImage', profileImage);
    } else if (Array.isArray(profileImage) && profileImage.length > 0 && profileImage[0] instanceof File) {
      formData.append('profileImage', profileImage[0]);
    }
    // If it's a string (existing URL in edit mode), don't append it - backend will keep existing
  
    // Same for aadharImage
    if (aadharImage instanceof File) {
      formData.append('aadharImage', aadharImage);
    } else if (Array.isArray(aadharImage) && aadharImage.length > 0 && aadharImage[0] instanceof File) {
      formData.append('aadharImage', aadharImage[0]);
    }
  
    formData.append('bloodGroup', bloodGroup);
    formData.append('name', formValues.name);
    formData.append('mobile', formValues.mobile);
    formData.append('email', formValues.email);
    formData.append('guardianName', formValues.guardianName);
    formData.append('guardianMobile', formValues.guardianMobile);
    formData.append('country', country);
    formData.append('state', states);
    formData.append('city', city);
    formData.append('address', formValues.address);
    formData.append('panNo', formValues.panNo);
    formData.append('dob', formValues.dob);
    formData.append('adminId', admin);
    // Ensure backend receives an array of strings
    const cleanedPins = Array.from(
      new Set(
        (selectedPincodes || [])
          .filter((p) => p)
          .map((p) => String(p).trim())
          .filter((p) => /^\d{6}$/.test(p))
      )
    );
    formData.append('pinCode', JSON.stringify(cleanedPins));
  
    try {
      const res = state?.id
        ? await updateAdmin({ formData, id: state.id }).unwrap()
        : await addAdmin(formData).unwrap();

      const { status, message } = res || {};
      if (status) {
        navigate('/sub-admin');
        showMessage('success', message || 'Sub admin saved successfully');
      } else {
        showMessage('error', message || 'Failed to save sub admin');
      }
    } catch (error) {
      console.error('Failed to save the sub admin:', error);
      // Try to surface actual backend validation details (Sequelize/Joi/etc)
      const backendMsg =
        error?.data?.message ||
        error?.data?.error?.message ||
        (Array.isArray(error?.data?.error?.errors) ? error.data.error.errors?.[0]?.message : null) ||
        error?.error ||
        'An unexpected error occurred';
      showMessage('error', backendMsg);
    }
  };

  useEffect(() => {
    if (state) {
      setFormValues({
        id: state.id,
      });

    }
  }, [state]);

  useEffect(() => {


    if (subAdminDetails && state) {
      setFormValues({
        name: subAdminDetails?.name,
        mobile: subAdminDetails?.mobile,
        email: subAdminDetails?.email,
        guardianName: subAdminDetails?.guardianName,
        guardianMobile: subAdminDetails?.guardianMobile,
        address: subAdminDetails?.address,
        panNo: subAdminDetails?.panNo,
        dob: new Date(subAdminDetails?.dob).toISOString().split('T')[0],
        id: state.id,
      });
      setProfileImage(subAdminDetails?.profileImage);
      setAadharImage(subAdminDetails?.aadharImage);
      setBloodGroup(subAdminDetails?.bloodGroup);
      setStates(subAdminDetails?.state);
      setCity(subAdminDetails?.city);
      setAdmin(subAdminDetails?.adminId);
      const pincodes = subAdminDetails?.pinCodeData?.map(item => item.pincode) || [];

      setSelectedPincodes(pincodes || []);
    }
  }, [subAdminDetails, state]);

  return (
    <>
      <Stack flexDirection={'row'} gap={1} mb={1}>
        <BackButton />
        <Heading head='Add Sub Admin' />
      </Stack>
      <Box className='card_container'>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={'2px'}>User Photo</Typography>
            <UploadImage files={profileImage} setFiles={setProfileImage} />
            <ErrorMessage message={profileErrorMessage} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='Name' name="name" value={formValues.name} onChange={handleInputChange} />
            {errorMessages.name && <ErrorMessage message={errorMessages.name} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='Mobile' type='number' name="mobile" value={formValues.mobile} onChange={handleInputChange} />
            {errorMessages.mobile && <ErrorMessage message={errorMessages.mobile} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='Email' type='email' name="email" value={formValues.email} onChange={handleInputChange} />
            {errorMessages.email && <ErrorMessage message={errorMessages.email} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='Guardian Name' name="guardianName" value={formValues.guardianName} onChange={handleInputChange} />
            {errorMessages.guardianName && <ErrorMessage message={errorMessages.guardianName} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='Guardian Mobile' name="guardianMobile" value={formValues.guardianMobile} onChange={handleInputChange} />
            {errorMessages.guardianMobile && <ErrorMessage message={errorMessages.guardianMobile} />}
          </Grid>
          {
            adminRole === 1 &&
            <Grid item xs={12} sm={6} md={4}>
                <Select selectList={adminListLoading ? [] : adminList} labelinput="Select Admin" name="admin" value={admin} onChange={handleAdminChange} />
              {errorMessages.admin && <ErrorMessage message={errorMessages.admin} />}
            </Grid>
          }
          <Grid item xs={12}>
            <Input labelinput='Address' name="address" value={formValues.address} onChange={handleInputChange} />
            {errorMessages.address && <ErrorMessage message={errorMessages.address} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select selectList={countryList} labelinput='Country' name="country" value={country} onChange={handleCountryChange} />
            {errorMessages.country && <ErrorMessage message={errorMessages.country} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select selectList={stateLoading ? [] : stateList} disabled={adminRole === 1 ? false : true} labelinput='State' name="state" value={states} onChange={handleStateChange} />
            {errorMessages.state && <ErrorMessage message={errorMessages.state} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select selectList={cityLoading ? [] : cityList} disabled={adminRole === 1 ? false : true} labelinput='City' name="city" value={city} onChange={handleCityChange} />
            {errorMessages.city && <ErrorMessage message={errorMessages.city} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Input labelinput='Pan Card No' name="panNo" value={formValues.panNo} onChange={handleInputChange} />
            {errorMessages.panNo && <ErrorMessage message={errorMessages.panNo} />}
          </Grid>

          {/* Pincode Selection - Only show pincodes assigned to selected Admin */}
          {admin && (
            <Grid item xs={12}>
              <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={'2px'}>
                {isSelectedAdminSuper
                  ? 'Select Pincodes (All pincodes for selected City)'
                  : 'Select Pincodes (Assigned to Selected Admin)'}
              </Typography>
              <Autocomplete
                multiple
                id="pincode-admin-select"
                options={isSelectedAdminSuper ? cityPincodesForSuperAdmin : adminPincodes}
                loading={isSelectedAdminSuper ? cityExtraPincodeLoading : adminDetailLoading}
                disableCloseOnSelect
                getOptionLabel={(option) => option.pincode || ''}
                isOptionEqualToValue={(option, value) =>
                  (option?.pincode || '') === (value?.pincode || '')
                }
                value={(isSelectedAdminSuper ? cityPincodesForSuperAdmin : adminPincodes).filter((p) =>
                  selectedPincodes.includes(p.pincode)
                )}
                onChange={(event, newValue) => {
                  const pins = newValue.map((item) => item.pincode);
                  setSelectedPincodes(pins);
                  setErrorMessages(prev => ({ ...prev, pincode: '' }));
                }}
                getOptionDisabled={(option) => {
                  const ownerSubAdminId = pincodeOwnershipMap[option.pincode];
                  // If not assigned to any PinCodeAdmin yet, it's selectable
                  if (!ownerSubAdminId) return false;
                  // In edit mode, allow pincodes already assigned to THIS sub admin
                  if (state?.id && Number(ownerSubAdminId) === Number(state.id)) return false;
                  // Otherwise, disable pincodes already assigned to some other PinCodeAdmin
                  return true;
                }}
                renderOption={(props, option, { selected }) => {
                  const ownerSubAdminId = pincodeOwnershipMap[option.pincode];
                  const isAssignedToOther =
                    !!ownerSubAdminId && (!state?.id || Number(ownerSubAdminId) !== Number(state.id));

                  const { key, ...optionProps } = props;
                  return (
                    <li
                      key={key}
                      {...optionProps}
                      style={{
                        backgroundColor: isAssignedToOther ? '#e3f2fd' : '#ffffff',
                        opacity: isAssignedToOther ? 0.7 : 1,
                      }}
                    >
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                        disabled={isAssignedToOther}
                      />
                      <span>{option.pincode}</span>
                      {isAssignedToOther && (
                        <Typography component="span" fontSize="11px" color="text.secondary" ml={1}>
                          (Already assigned)
                        </Typography>
                      )}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      (isSelectedAdminSuper ? cityExtraPincodeLoading : adminDetailLoading)
                        ? "Loading pincodes..."
                        : "Search & select pincodes"
                    }
                  />
                )}
              />
              {errorMessages.pincode && <ErrorMessage message={errorMessages.pincode} />}
              {!isSelectedAdminSuper && !adminDetailLoading && adminPincodes.length === 0 && admin && (
                <Typography fontSize={{ xs: '11px', sm: '12px' }} color="text.secondary" mt={0.5}>
                  No pincodes assigned to this admin.
                </Typography>
              )}
              {isSelectedAdminSuper && !cityExtraPincodeLoading && city && cityPincodesForSuperAdmin.length === 0 && (
                <Typography fontSize={{ xs: '11px', sm: '12px' }} color="text.secondary" mt={0.5}>
                  No pincodes found for this city.
                </Typography>
              )}
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={'2px'}>Upload Aadhar Card</Typography>
            <UploadImage files={aadharImage} setFiles={setAadharImage} />
            <ErrorMessage message={addharErrorMessage} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Select selectList={bloodGroupList} labelinput='Blood Group' name="bloodGroup" value={bloodGroup} onChange={handleBloodGroupChange} />
            {errorMessages.bloodGroup && <ErrorMessage message={errorMessages.bloodGroup} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='User DOB' name="dob" type='date' value={formValues.dob} onChange={handleInputChange} />
            {errorMessages.dob && <ErrorMessage message={errorMessages.dob} />}
          </Grid>
          <Grid item xs={12}>
            <Stack flexDirection={'row'} gap={1}>
              <Button buttonName='save' onClick={handleFormSubmit} />
              <Button buttonName='cancel' color='white' onClick={() => navigate('/admin')} />
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default AddPincodeAdmin;